type StorageArea = 'localStorage' | 'sessionStorage';

type StorageSnapshot = Record<StorageArea, Record<string, string>>;

type NativeMessageHandler = (message: any) => void;

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    __BBR_WEBVIEW_STORAGE__?: Partial<StorageSnapshot>;
  }
}

const readStorage = (storage: Storage): Record<string, string> => {
  const snapshot: Record<string, string> = {};

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) continue;

    const value = storage.getItem(key);
    if (value !== null) {
      snapshot[key] = value;
    }
  }

  return snapshot;
};

export const isInWebView = () =>
  typeof window !== 'undefined' && Boolean(window.ReactNativeWebView?.postMessage);

export const getStorageSnapshot = (): StorageSnapshot => ({
  localStorage: readStorage(window.localStorage),
  sessionStorage: readStorage(window.sessionStorage),
});

export const postToNative = (type: string, payload: Record<string, any> = {}) => {
  if (!isInWebView()) {
    return;
  }

  window.ReactNativeWebView?.postMessage(
    JSON.stringify({
      type,
      ...payload,
    }),
  );
};

export const notifyWebViewReady = () => {
  postToNative('WEBVIEW_READY', {
    storage: getStorageSnapshot(),
  });
};

export const notifyStorageSet = (storage: StorageArea, key: string, value: string) => {
  postToNative('WEBVIEW_STORAGE_SET', {
    storage,
    key,
    value,
    snapshot: getStorageSnapshot(),
  });
};

export const notifyStorageRemove = (storage: StorageArea, key: string) => {
  postToNative('WEBVIEW_STORAGE_REMOVE', {
    storage,
    key,
    snapshot: getStorageSnapshot(),
  });
};

export const notifyStorageClear = (storage: StorageArea | 'all') => {
  postToNative('WEBVIEW_STORAGE_CLEAR', {
    storage,
    snapshot: getStorageSnapshot(),
  });
};

export const onNativeMessage = (handler: NativeMessageHandler) => {
  const listener = (event: Event) => {
    const data = 'data' in event ? (event as MessageEvent).data : undefined;
    if (typeof data !== 'string') return;

    try {
      handler(JSON.parse(data));
    } catch {
      // Ignore messages that are not meant for this bridge.
    }
  };

  window.addEventListener('message', listener);
  document.addEventListener('message', listener);

  return () => {
    window.removeEventListener('message', listener);
    document.removeEventListener('message', listener);
  };
};

export const applyNativeStorageSnapshot = (storage?: Partial<StorageSnapshot>) => {
  if (!storage) return;

  Object.entries(storage.localStorage || {}).forEach(([key, value]) => {
    window.localStorage.setItem(key, value);
  });

  Object.entries(storage.sessionStorage || {}).forEach(([key, value]) => {
    window.sessionStorage.setItem(key, value);
  });
};

export const applyPreloadedNativeStorageSnapshot = () => {
  if (!window.__BBR_WEBVIEW_STORAGE__) return;

  applyNativeStorageSnapshot(window.__BBR_WEBVIEW_STORAGE__);
  delete window.__BBR_WEBVIEW_STORAGE__;
};
